import enUS from 'lngProvider/locales/en_US.json';
import esES from 'lngProvider/locales/es_ES.json';

const BUNDLES = {
  en: enUS,
  es: esES,
};

/**
 * Extract Firebase Auth error code from an Error-like object.
 * @param {import('firebase/auth').AuthError | Error | { code?: string; message?: string } | null | undefined} error
 * @returns {string | null}
 */
export function getFirebaseAuthErrorCode(error) {
  if (!error) {
    return null;
  }
  if (typeof error.code === 'string' && error.code.startsWith('auth/')) {
    return error.code;
  }
  const msg = typeof error.message === 'string' ? error.message : '';
  const match = msg.match(/auth\/[a-z0-9-]+/i);
  return match ? match[0] : null;
}

/**
 * Map Firebase Auth error codes to react-intl message ids (auth.error.*).
 * Same user-facing copy for similar codes where Firebase intentionally
 * does not distinguish (e.g. invalid-credential).
 */
const CODE_TO_MESSAGE_ID = {
  'auth/invalid-email': 'auth.error.invalidEmail',
  'auth/user-disabled': 'auth.error.userDisabled',
  'auth/user-not-found': 'auth.error.invalidCredentials',
  'auth/wrong-password': 'auth.error.invalidCredentials',
  'auth/invalid-credential': 'auth.error.invalidCredentials',
  'auth/invalid-login-credentials': 'auth.error.invalidCredentials',
  'auth/account-exists-with-different-credential': 'auth.error.accountExistsDifferentCredential',
  'auth/too-many-requests': 'auth.error.tooManyRequests',
  'auth/network-request-failed': 'auth.error.network',
  'auth/email-already-in-use': 'auth.error.emailAlreadyInUse',
  'auth/weak-password': 'auth.error.weakPassword',
  'auth/operation-not-allowed': 'auth.error.operationNotAllowed',
  'auth/popup-blocked': 'auth.error.popupBlocked',
  'auth/popup-closed-by-user': 'auth.error.popupClosed',
  'auth/cancelled-popup-request': 'auth.error.popupClosed',
  'auth/internal-error': 'auth.error.generic',
  'auth/invalid-api-key': 'auth.error.generic',
  'auth/app-deleted': 'auth.error.generic',
  'auth/invalid-action-code': 'auth.error.emailLinkInvalidOrExpired',
  'auth/expired-action-code': 'auth.error.emailLinkInvalidOrExpired',
  'auth/missing-email': 'auth.error.emailLinkMissingEmail',
};

/**
 * @param {string} locale 'en' | 'es'
 * @param {string} messageId
 * @returns {string}
 */
function translate(locale, messageId) {
  const bundle = BUNDLES[locale === 'es' ? 'es' : 'en'];
  return bundle[messageId] || BUNDLES.en[messageId] || BUNDLES.en['auth.error.generic'];
}

/**
 * User-facing message for Firebase Auth errors (no vendor strings).
 * @param {import('firebase/auth').AuthError | Error | { code?: string; message?: string } | null | undefined} error
 * @param {string} [locale='en'] 'en' | 'es'
 * @returns {string}
 */
export function resolveFirebaseAuthErrorMessage(error, locale = 'en') {
  const code = getFirebaseAuthErrorCode(error);
  const messageId = (code && CODE_TO_MESSAGE_ID[code]) || 'auth.error.generic';
  return translate(locale, messageId);
}
